import { isHomepageSurfacePath, isListingsSurfacePath } from '@/lib/document/surfaceScripts';

import type { DatadogRumConfig } from './config';
import { normalizeRouteTemplate } from './route';

export const DATADOG_RUM_SCRIPT_URL = 'https://www.datadoghq-browser-agent.com/us1/v6/datadog-rum.js';

export const LISTINGS_RUM_SURFACE = 'listings_webapp';
export const HOMEPAGE_RUM_SURFACE = 'homepage_webapp';

const RUM_SURFACES = [LISTINGS_RUM_SURFACE, HOMEPAGE_RUM_SURFACE] as const;

function isRumSurface(value: unknown): value is (typeof RUM_SURFACES)[number] {
  return typeof value === 'string' && (RUM_SURFACES as readonly string[]).includes(value);
}

export type DatadogRum = {
  getInitConfiguration?: () => unknown;
  init: (configuration: Record<string, unknown>) => void;
  setTrackingConsent?: (consent: 'granted' | 'not-granted') => void;
  setGlobalContextProperty: (name: string, value: string) => void;
  removeGlobalContextProperty?: (name: string) => void;
  addAction?: (name: string, data: unknown) => void;
  onReady?: (callback: () => void) => void;
  addError?: (error: unknown, context?: object) => void;
};

let initialized = false;

declare global {
  interface Window {
    DD_RUM?: DatadogRum;
    __EB_WEB_CONSENT__?: { analytics?: boolean };
  }
}

export function hasAnalyticsConsent(): boolean {
  return typeof window !== 'undefined' && window.__EB_WEB_CONSENT__?.analytics === true;
}

export function setRumTrackingConsent(
  rum: DatadogRum | undefined,
  consent: 'granted' | 'not-granted',
): void {
  rum?.setTrackingConsent?.(consent);
}

type RumEventRecord = Record<string, unknown>;

function isRecord(value: unknown): value is RumEventRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function scrubRoute(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length === 0) {
    return undefined;
  }

  try {
    const url = new URL(value);
    return normalizeRouteTemplate(url.pathname);
  } catch {
    return normalizeRouteTemplate(value);
  }
}

function scrubRouteField(record: RumEventRecord, field: string): void {
  const route = scrubRoute(record[field]);
  if (route) {
    record[field] = route;
  } else {
    delete record[field];
  }
}

function scrubResource(resource: unknown): void {
  if (isRecord(resource)) {
    scrubRouteField(resource, 'url');
  }
}

function scrubError(event: RumEventRecord): void {
  const error = isRecord(event.error) ? event.error : undefined;
  const resource = error && isRecord(error.resource) ? error.resource : undefined;
  const resourceUrl = resource && scrubRoute(resource.url);
  const message = typeof error?.message === 'string' && error.message.length > 0
    ? error.message
    : 'client_error';
  const stack = typeof error?.stack === 'string' && error.stack.length > 0
    ? error.stack
    : undefined;

  event.error = {
    message,
    ...(stack ? { stack } : {}),
    ...(resourceUrl ? { resource: { url: resourceUrl } } : {}),
  };
}

function scrubLongTask(longTask: unknown): void {
  if (!isRecord(longTask)) {
    return;
  }

  if (!Array.isArray(longTask.scripts)) {
    delete longTask.scripts;
    return;
  }

  const scripts = longTask.scripts.flatMap((script) => {
    const sourceUrl = scrubRoute(
      isRecord(script) ? script.source_url ?? script.url : script,
    );
    return sourceUrl ? [{ source_url: sourceUrl }] : [];
  });

  if (scripts.length > 0) {
    longTask.scripts = scripts;
  } else {
    delete longTask.scripts;
  }
}

function scrubAction(action: unknown): void {
  if (!isRecord(action)) {
    return;
  }

  scrubRouteField(action, 'name');
  if (isRecord(action.target)) {
    scrubRouteField(action.target, 'name');
  } else {
    delete action.target;
  }
}

function scrubView(view: RumEventRecord): void {
  scrubRouteField(view, 'url');
  scrubRouteField(view, 'name');
  scrubRouteField(view, 'referrer');
  for (const field of Object.keys(view)) {
    if (field.startsWith('url_') || field.startsWith('referrer_')) {
      delete view[field];
    }
  }
}

function scrubContext(event: RumEventRecord): void {
  if (!isRecord(event.context)) {
    delete event.context;
    return;
  }

  const context: RumEventRecord = {};
  for (const field of ['application', 'stage', 'release']) {
    const value = event.context[field];
    if (typeof value === 'string') {
      context[field] = value;
    }
  }
  if (isRumSurface(event.context.surface)) {
    context.surface = event.context.surface;
  }

  if (Object.keys(context).length > 0) {
    event.context = context;
  } else {
    delete event.context;
  }
}

export function scrubRumEvent(event: RumEventRecord): boolean {
  if (isRecord(event.view)) {
    scrubView(event.view);
  } else {
    delete event.view;
  }

  if (isRecord(event.resource)) {
    scrubResource(event.resource);
  } else {
    delete event.resource;
  }

  if ('error' in event) {
    scrubError(event);
  }

  if (isRecord(event.long_task)) {
    scrubLongTask(event.long_task);
  } else {
    delete event.long_task;
  }

  if (isRecord(event.action)) {
    scrubAction(event.action);
  } else {
    delete event.action;
  }

  scrubContext(event);
  delete event.usr;
  delete event.account;

  return true;
}

export function rumSurfaceForPathname(pathname: string): string | undefined {
  if (isListingsSurfacePath(pathname)) {
    return LISTINGS_RUM_SURFACE;
  }
  if (isHomepageSurfacePath(pathname)) {
    return HOMEPAGE_RUM_SURFACE;
  }
  return undefined;
}

function applyRumSurface(rum: DatadogRum, pathname: string | undefined): void {
  if (!pathname) {
    return;
  }
  const surface = rumSurfaceForPathname(pathname);
  if (surface) {
    rum.setGlobalContextProperty('surface', surface);
  } else {
    rum.removeGlobalContextProperty?.('surface');
  }
}

export function syncRumSurface(rum: DatadogRum | undefined, pathname: string | undefined): void {
  if (!rum) {
    return;
  }
  applyRumSurface(rum, pathname);
}

export function initializeRum(
  config: DatadogRumConfig | undefined,
  rum: DatadogRum | undefined,
  pathname?: string,
): boolean {
  if (!config || !rum) {
    return false;
  }
  if (initialized || rum.getInitConfiguration?.()) {
    applyRumSurface(rum, pathname);
    setRumTrackingConsent(rum, 'granted');
    return false;
  }

  try {
    rum.setGlobalContextProperty('application', 'eb-web');
    rum.setGlobalContextProperty('stage', config.env);
    rum.setGlobalContextProperty('release', config.version);
    applyRumSurface(rum, pathname);
    rum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: 'datadoghq.com',
      service: config.service,
      env: config.env,
      version: config.version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackResources: false,
      trackUserInteractions: false,
      trackingConsent: 'granted',
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [],
      beforeSend: scrubRumEvent,
    });
    initialized = true;
    return true;
  } catch {
    return false;
  }
}

export function resetRumForTests(): void {
  initialized = false;
}
